import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Archive, AtSign, Bell, CheckCheck, Shield } from 'lucide-react';

import { API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import {
  approvalRequestService,
  ApprovalRequestServiceError,
  type ApprovalRequestStatus,
  type ApprovalRequestScope,
} from '@/features/projects/services/approvalRequestService';
import { usePresenceTransition } from '@/shared/hooks/usePresenceTransition';
import { applyInboxNotificationPatch, isInboxNotification } from '@/app/utils/inboxNotificationState';
import { parseServerDateTime } from '@/shared/utils/serverTime';

export interface MentionNotification {
  id: string;
  notification_type: 'mention';
  recipient_user_id: string;
  actor_user_id: string;
  actor_username: string;
  project_id: string;
  project_name: string;
  node_id: string;
  node_type: string;
  node_title: string;
  field_name: string;
  excerpt: string;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
  read_at: string | null;
}

export interface ApprovalInboxNotification {
  id: string;
  notification_type: 'approval';
  project_id: string;
  project_name?: string | null;
  node_id?: string | null;
  node_type?: string | null;
  node_title?: string | null;
  scope: ApprovalRequestScope;
  current_visibility_tier: 'internal' | 'partner' | 'client';
  requested_visibility_tier: 'internal' | 'partner' | 'client';
  requester_user_id: string;
  requester_username?: string | null;
  reviewer_user_id?: string | null;
  reviewer_username?: string | null;
  rationale: string;
  status: ApprovalRequestStatus;
  execution_error?: string | null;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
  requested_at?: string | null;
  reviewed_at?: string | null;
  executed_at?: string | null;
  audience_role: 'admin-review' | 'requester-status';
}

export type InboxNotification = MentionNotification | ApprovalInboxNotification;

interface MentionInboxPopoverProps {
  theme: ThemeClasses;
  isDarkMode: boolean;
  /** Quieter 32px trigger with a 16px bell, used inside SAI Projects. */
  compact?: boolean;
  onOpenNotificationTarget: (notification: InboxNotification) => Promise<void> | void;
}

interface NotificationsResponse {
  notifications: InboxNotification[];
  unread_count: number;
}

const formatTimestamp = (value: string, locale: string) => {
  const parsed = parseServerDateTime(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString(locale);
};

// Scopes and the bare "approval" type are backend identifiers; only translated labels reach the UI.
const APPROVAL_SCOPE_LABELS: Record<ApprovalRequestScope, string> = {
  node: 'Node',
  subtree: 'Subtree',
  project: 'Project',
  'project-rewrite': 'Project rewrite',
};
const approvalScopeLabel = (scope: ApprovalRequestScope): string => APPROVAL_SCOPE_LABELS[scope] || scope;
const notificationTypeLabel = (notification: InboxNotification): string => (
  notification.node_type || (notification.notification_type === 'approval' ? 'Approval' : 'Mention')
);

class InboxRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'InboxRequestError';
    this.status = status;
  }
}

// The server answers 404 for an approval row the user can no longer act on (decided by another
// admin, or archived elsewhere). Such a row is stale, not broken: drop it instead of leaving it dead.
const isStaleNotificationError = (error: unknown): boolean => (
  error instanceof InboxRequestError && error.status === 404
);

export const MentionInboxPopover: React.FC<MentionInboxPopoverProps> = ({
  theme,
  isDarkMode: _isDarkMode,
  compact = false,
  onOpenNotificationTarget,
}) => {
  const { t, locale } = useSettings();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const { shouldRender: shouldRenderPopover, isActive: isPopoverActive } = usePresenceTransition(isOpen, { duration: 160 });
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<InboxNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [listError, setListError] = useState<string | null>(null);
  const stateRevisionRef = useRef(0);
  const loadRevisionRef = useRef(0);
  const notificationRevisionRef = useRef(new Map<string, number>());

  const isMentionNotification = (notification: InboxNotification): notification is MentionNotification =>
    notification.notification_type === 'mention';

  const loadNotifications = useCallback(async () => {
    const loadRevision = ++loadRevisionRef.current;
    const stateRevisionAtStart = stateRevisionRef.current;
    setIsLoading(true);
    try {
      const response = await authService.authenticatedFetch(
        API_ENDPOINTS.auth.notifications({ limit: 50 }),
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to load notifications: ${response.status}`);
      }
      const payload = (await response.json()) as NotificationsResponse;
      if (loadRevision !== loadRevisionRef.current) {
        return;
      }
      if (stateRevisionRef.current === stateRevisionAtStart) {
        setNotifications(payload.notifications || []);
        setUnreadCount(payload.unread_count || 0);
      } else {
        // A push/local mutation that happened after this request is newer than
        // its snapshot. Keep those entries authoritative while still filling
        // in items that only appeared in the HTTP response.
        setNotifications((current) => {
          const incoming = payload.notifications || [];
          const incomingIds = new Set(incoming.map((notification) => notification.id));
          const currentById = new Map(current.map((notification) => [notification.id, notification]));
          const changedSinceRequest = (id: string) =>
            (notificationRevisionRef.current.get(id) || 0) > stateRevisionAtStart;
          const resolved = incoming.flatMap((notification) => {
            if (!changedSinceRequest(notification.id)) return [notification];
            const currentNotification = currentById.get(notification.id);
            // Absence after a newer archive is authoritative too.
            return currentNotification ? [currentNotification] : [];
          });
          return [
            ...current.filter((notification) => changedSinceRequest(notification.id) && !incomingIds.has(notification.id)),
            ...resolved,
          ];
        });
      }
    } catch (error) {
      console.error('Failed to load inbox notifications', error);
    } finally {
      if (loadRevision === loadRevisionRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      const focusWasInside = containerRef.current?.contains(document.activeElement) ?? false;
      setIsOpen(false);
      if (focusWasInside) {
        triggerRef.current?.focus({ preventScroll: true });
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleInboxNotification = (event: Event) => {
      stateRevisionRef.current += 1;
      const detail = (event as CustomEvent<{
        notification?: InboxNotification;
        unread_count?: number;
        unreadCount?: number;
      }>).detail;
      const notification = detail?.notification;
      const nextUnreadCount = detail?.unread_count ?? detail?.unreadCount;

      if (isInboxNotification(notification)) {
        notificationRevisionRef.current.set(notification.id, stateRevisionRef.current);
        setNotifications((current) => {
          const deduped = current.filter((item) => item.id !== notification.id);
          return [notification, ...deduped];
        });
      } else {
        void loadNotifications();
      }

      if (typeof nextUnreadCount === 'number') {
        setUnreadCount(nextUnreadCount);
      } else if (isInboxNotification(notification) && !notification.is_read) {
        setUnreadCount((current) => current + 1);
      }
    };

    window.addEventListener('mention-notification', handleInboxNotification as EventListener);
    window.addEventListener('inbox-notification', handleInboxNotification as EventListener);
    return () => {
      window.removeEventListener('mention-notification', handleInboxNotification as EventListener);
      window.removeEventListener('inbox-notification', handleInboxNotification as EventListener);
    };
  }, [loadNotifications]);

  const updateNotificationState = useCallback((
    notificationId: string,
    patch: { is_read?: boolean; is_archived?: boolean; read_at?: string | null },
  ) => {
    stateRevisionRef.current += 1;
    notificationRevisionRef.current.set(notificationId, stateRevisionRef.current);
    setNotifications((current) =>
      current.map((notification) => (
        notification.id === notificationId
          ? applyInboxNotificationPatch(notification, patch)
          : notification
      ))
    );
  }, []);

  const setRowError = useCallback((notificationId: string, message: string | null) => {
    setRowErrors((current) => {
      if (message === null) {
        if (!(notificationId in current)) return current;
        const next = { ...current };
        delete next[notificationId];
        return next;
      }
      return { ...current, [notificationId]: message };
    });
  }, []);

  const removeStaleNotification = useCallback((notificationId: string) => {
    stateRevisionRef.current += 1;
    notificationRevisionRef.current.set(notificationId, stateRevisionRef.current);
    setNotifications((current) => current.filter((notification) => notification.id !== notificationId));
    setRowError(notificationId, null);
    void loadNotifications();
  }, [loadNotifications, setRowError]);

  const markRead = useCallback(async (notificationId: string) => {
    const response = await authService.authenticatedFetch(
      API_ENDPOINTS.auth.readNotification(notificationId),
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new InboxRequestError(response.status, `Failed to mark notification ${notificationId} as read`);
    }
    const payload = await response.json();
    updateNotificationState(notificationId, { is_read: true });
    if (typeof payload.unread_count === 'number') {
      setUnreadCount(payload.unread_count);
    }
  }, [updateNotificationState]);

  const archiveNotification = useCallback(async (notificationId: string) => {
    const response = await authService.authenticatedFetch(
      API_ENDPOINTS.auth.archiveNotification(notificationId),
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new InboxRequestError(response.status, `Failed to archive notification ${notificationId}`);
    }
    const payload = await response.json();
    stateRevisionRef.current += 1;
    notificationRevisionRef.current.set(notificationId, stateRevisionRef.current);
    setNotifications((current) => current.filter((notification) => notification.id !== notificationId));
    if (typeof payload.unread_count === 'number') {
      setUnreadCount(payload.unread_count);
    }
  }, []);

  const handleArchive = useCallback(async (notificationId: string) => {
    setRowError(notificationId, null);
    try {
      await archiveNotification(notificationId);
    } catch (error) {
      if (isStaleNotificationError(error)) {
        removeStaleNotification(notificationId);
        return;
      }
      console.error('Failed to archive notification', error);
      setRowError(notificationId, t('Could not archive this notification. Try again.'));
    }
  }, [archiveNotification, removeStaleNotification, setRowError, t]);

  const handleOpenNotification = useCallback(async (notification: InboxNotification) => {
    setListError(null);
    if (!notification.is_read) {
      try {
        await markRead(notification.id);
      } catch (error) {
        // Marking read is bookkeeping; the target must still open.
        if (isStaleNotificationError(error)) {
          removeStaleNotification(notification.id);
        } else {
          console.error('Failed to mark notification as read', error);
        }
      }
    }
    try {
      await onOpenNotificationTarget(notification);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to open notification target', error);
      setListError(t('Could not open this notification. Try again.'));
    }
  }, [markRead, onOpenNotificationTarget, removeStaleNotification, t]);

  const handleReviewApproval = useCallback(async (
    notification: ApprovalInboxNotification,
    decision: 'approve' | 'reject',
  ) => {
    setRowError(notification.id, null);
    setListError(null);
    try {
      if (decision === 'approve') {
        await approvalRequestService.approve(notification.id);
      } else {
        await approvalRequestService.reject(notification.id);
      }
      await loadNotifications();
    } catch (error) {
      console.error(`Failed to ${decision} approval request`, error);
      const status = error instanceof ApprovalRequestServiceError ? error.status : undefined;
      if (status === 400 || status === 404) {
        // Decided by another reviewer: the server no longer lists it, so refresh and say why the row went.
        setListError(t('This approval request was already processed by another reviewer.'));
        void loadNotifications();
        return;
      }
      if (status === 409) {
        setRowError(notification.id, t('The reviewed content changed since this request was made. Ask the requester for a fresh approval request.'));
        return;
      }
      setRowError(notification.id, decision === 'approve' ? t('Failed to approve request') : t('Failed to reject request'));
    }
  }, [loadNotifications, setRowError, t]);

  const handleMarkAllRead = useCallback(async () => {
    const revisionAtStart = stateRevisionRef.current;
    const idsAtStart = new Set(notifications.map(notification => notification.id));
    setListError(null);
    try {
      const response = await authService.authenticatedFetch(
        API_ENDPOINTS.auth.readAllNotifications,
        { method: 'POST' }
      );
      if (!response.ok) {
        throw new Error(`Failed to mark all notifications as read`);
      }
      const payload = await response.json().catch(() => null);
      const receivedNewerState = stateRevisionRef.current !== revisionAtStart;
      stateRevisionRef.current += 1;
      const mutationRevision = stateRevisionRef.current;
      const settledIds = new Set([...idsAtStart].filter(id =>
        (notificationRevisionRef.current.get(id) || 0) <= revisionAtStart,
      ));
      settledIds.forEach(id => notificationRevisionRef.current.set(id, mutationRevision));
      setNotifications((current) => current.map((notification) => {
        if (!settledIds.has(notification.id)) {
          return notification;
        }
        return applyInboxNotificationPatch(notification, { is_read: true });
      }));
      if (receivedNewerState) {
        // The response predates a push or local action. Preserve those rows
        // and the newer badge until an authoritative refresh reconciles them.
        void loadNotifications();
      } else {
        setUnreadCount(typeof payload?.unread_count === 'number' ? payload.unread_count : 0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
      setListError(t('Could not mark all notifications as read. Try again.'));
    }
  }, [loadNotifications, notifications, t]);

  const renderedNotifications = useMemo(
    () => notifications.filter((notification) => !notification.is_archived),
    [notifications]
  );

  return (
    <div ref={containerRef} className={`relative flex ${compact ? 'w-8' : 'w-10'} shrink-0 justify-center`}>
      <button
        ref={triggerRef}
        type="button"
        data-testid="app-inbox-trigger"
        onClick={() => setIsOpen((current) => !current)}
        className={cn(
          `relative flex ${compact ? 'h-8 w-8 rounded-full' : 'h-9 w-9 rounded-md'} items-center justify-center border border-transparent transition-colors`,
          theme.textSecondary,
          theme.hover
        )}
        aria-label={t('Inbox')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={shouldRenderPopover ? popoverId : undefined}
        title={t('Inbox')}
      >
        <Bell size={compact ? 16 : 20} strokeWidth={compact ? 1.75 : 2} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 min-w-[1.1rem] rounded-full bg-red-600 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      {shouldRenderPopover ? (
        <div
          id={popoverId}
          role="dialog"
          aria-label={t('Inbox')}
          data-testid="app-inbox-popover"
          className={cn(
            'absolute right-0 top-12 z-[1200] w-[26rem] origin-top-right overflow-hidden rounded-lg border shadow-2xl transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
            isPopoverActive ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-1 scale-[0.985] opacity-0',
            theme.card,
            theme.borderPrimary
          )}
        >
          <div className={cn('flex items-center justify-between border-b px-4 py-3', theme.bgSecondary, theme.borderPrimary)}>
            <div>
              <div className={cn('text-sm font-semibold', theme.textPrimary)}>{t('Inbox')}</div>
              <div className={cn('text-xs', theme.textMuted)}>{t('{{count}} unread', { count: unreadCount })}</div>
            </div>
            <button
              type="button"
              onClick={handleMarkAllRead}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors',
                theme.textSecondary,
                theme.hover
              )}
            >
              <CheckCheck size={14} />
              {t('Mark all read')}
            </button>
          </div>

          <div className={cn('max-h-[28rem] overflow-y-auto', theme.bgSecondary)}>
            {listError ? (
              <div
                role="alert"
                data-testid="app-inbox-error"
                className={cn('flex items-start justify-between gap-3 border-b px-4 py-2 text-xs text-red-600 dark:text-red-300', theme.borderPrimary)}
              >
                <span>{listError}</span>
                <button
                  type="button"
                  onClick={() => setListError(null)}
                  className={cn('shrink-0 rounded px-1 text-[11px] underline', theme.textMuted)}
                >
                  {t('Dismiss')}
                </button>
              </div>
            ) : null}

            {isLoading && renderedNotifications.length === 0 ? (
              <div className={cn('px-4 py-6 text-sm', theme.textMuted)}>{t('Loading notifications...')}</div>
            ) : null}

            {!isLoading && renderedNotifications.length === 0 ? (
              <div className={cn('px-4 py-8 text-center text-sm', theme.textMuted)}>
                {t('No inbox items yet.')}
              </div>
            ) : null}

            {renderedNotifications.map((notification) => (
              <div
                key={notification.id}
                data-testid={
                  isMentionNotification(notification)
                    ? `mention-inbox-item-${notification.id}`
                    : `approval-inbox-item-${notification.id}`
                }
                className={cn(
                  'flex w-full flex-col gap-2 border-b px-4 py-3 text-left',
                  theme.borderPrimary,
                  !notification.is_read
                    ? cn(theme.surfaceSelected, theme.selectionRing)
                    : ''
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  {/* The row itself is not a button: a button may not contain the archive/approve controls. */}
                  <button
                    type="button"
                    data-testid={`app-inbox-open-${notification.id}`}
                    onClick={() => void handleOpenNotification(notification)}
                    className={cn('min-w-0 flex-1 rounded-md text-left transition-colors', theme.hover)}
                  >
                    {isMentionNotification(notification) ? (
                      <>
                        <div className={cn('flex items-center gap-2 text-sm', theme.textPrimary)}>
                          <AtSign size={14} className="shrink-0" />
                          <span className="truncate font-medium">{notification.node_title || t('View')}</span>
                        </div>
                        <div className={cn('mt-1 text-xs', theme.textMuted)}>
                          {t('{{actor}} mentioned you in {{project}}', {
                            actor: notification.actor_username,
                            project: notification.project_name,
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={cn('flex items-center gap-2 text-sm', theme.textPrimary)}>
                          <Shield size={14} className="shrink-0" />
                          <span className="truncate font-medium">
                            {notification.node_title || notification.project_name || t('Approval request')}
                          </span>
                        </div>
                        <div className={cn('mt-1 text-xs', theme.textMuted)}>
                          {notification.audience_role === 'admin-review'
                            ? t('{{requester}} requested {{visibility}} access', {
                                requester: notification.requester_username || t('Employee'),
                                visibility: t(notification.requested_visibility_tier),
                              })
                            : notification.status === 'executed'
                              ? t('Approved and executed by {{reviewer}}', {
                                  reviewer: notification.reviewer_username || t('Admin'),
                                })
                              : notification.status === 'rejected'
                                ? t('Rejected by {{reviewer}}', {
                                    reviewer: notification.reviewer_username || t('Admin'),
                                  })
                                : t('Execution failed after approval')}
                        </div>
                      </>
                    )}
                    {isMentionNotification(notification) ? (
                      notification.excerpt ? (
                        <div className={cn('mt-2 line-clamp-2 text-xs', theme.textSecondary)}>{notification.excerpt}</div>
                      ) : null
                    ) : (
                      <div className={cn('mt-2 space-y-1 text-xs', theme.textSecondary)}>
                        <div className="line-clamp-2">{notification.rationale}</div>
                        <div className={theme.textMuted}>
                          {t('{{current}} → {{requested}} · {{scope}}', {
                            current: t(notification.current_visibility_tier),
                            requested: t(notification.requested_visibility_tier),
                            scope: t(approvalScopeLabel(notification.scope)),
                          })}
                        </div>
                        {notification.execution_error ? (
                          <div className="line-clamp-2 text-red-500">{notification.execution_error}</div>
                        ) : null}
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    data-testid={`app-inbox-archive-${notification.id}`}
                    onClick={() => void handleArchive(notification.id)}
                    className={cn(
                      'shrink-0 rounded-md p-1 transition-colors',
                      theme.textMuted,
                      theme.hover
                    )}
                    aria-label={t('Archive notification')}
                    title={t('Archive notification')}
                  >
                    <Archive size={14} />
                  </button>
                </div>
                {!isMentionNotification(notification) && notification.audience_role === 'admin-review' && notification.status === 'pending' ? (
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      data-testid={`approval-inbox-approve-${notification.id}`}
                      onClick={() => void handleReviewApproval(notification, 'approve')}
                      className={cn('rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors', theme.primaryAction)}
                    >
                      {t('Approve and execute')}
                    </button>
                    <button
                      type="button"
                      data-testid={`approval-inbox-reject-${notification.id}`}
                      onClick={() => void handleReviewApproval(notification, 'reject')}
                      className={cn('rounded-md px-2.5 py-1 text-[11px] transition-colors', theme.hover, theme.textSecondary)}
                    >
                      {t('Reject')}
                    </button>
                  </div>
                ) : null}
                {rowErrors[notification.id] ? (
                  <div
                    role="alert"
                    data-testid={`app-inbox-item-error-${notification.id}`}
                    className="text-xs text-red-600 dark:text-red-300"
                  >
                    {rowErrors[notification.id]}
                  </div>
                ) : null}
                <div className={cn('flex items-center justify-between text-[11px]', theme.textMuted)}>
                  <span>{t(notificationTypeLabel(notification))}</span>
                  <span>{formatTimestamp(notification.created_at, locale)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
